import CustomImage from '@/components/common/custom-image'
import React, { useState } from 'react'
import type { ChangeEvent, FormEvent } from 'react'
import Icons from '@/components/icons'
import cartApis from '@/apis/cart'
import { useMessageBus } from '@/components/ErrorMessage'

interface FormData {
  fullName: string
  mobile: string
  email: string
}

type FormErrors = Record<keyof FormData, string>

const INITIAL_FORM_DATA: FormData = {
  fullName: '',
  mobile: '',
  email: ''
}

const INITIAL_ERRORS: FormErrors = {
  fullName: '',
  mobile: '',
  email: ''
}

const EnquiryForm = (): JSX.Element => {
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM_DATA)
  const [errors, setErrors] = useState<FormErrors>(INITIAL_ERRORS)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const { showError, showSuccess } = useMessageBus()

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target
    const field = name as keyof FormData

    if (field === 'mobile') {
      const numericValue = value.replace(/\D/g, '')
      if (numericValue.length <= 10) {
        setFormData(prev => ({ ...prev, mobile: numericValue }))
      }
    } else {
      setFormData(prev => ({ ...prev, [field]: value }))
    }

    if (errors[field].length > 0) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const validate = (): boolean => {
    const tempErrors: FormErrors = { ...INITIAL_ERRORS }
    let isValid = true

    if (formData.fullName.trim().length === 0) {
      tempErrors.fullName = 'Full Name is required'
      isValid = false
    }

    if (formData.mobile.length === 0) {
      tempErrors.mobile = 'Mobile Number is required'
      isValid = false
    } else if (formData.mobile.length !== 10) {
      tempErrors.mobile = 'Please enter a valid 10-digit number'
      isValid = false
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (formData.email.trim().length === 0) {
      tempErrors.email = 'Email is required'
      isValid = false
    } else if (!emailRegex.test(formData.email)) {
      tempErrors.email = 'Please enter a valid email address'
      isValid = false
    }

    setErrors(tempErrors)
    return isValid
  }

  const handleSubmit = async (
    e: FormEvent<HTMLFormElement>
  ): Promise<void> => {
    e.preventDefault()

    if (!validate()) return

    setIsSubmitting(true)

    try {
      await cartApis.callbackRequest({
        name: formData.fullName,
        email: formData.email,
        phoneNumber: formData.mobile
      })

      // success handling
      setFormData(INITIAL_FORM_DATA)
      setErrors(INITIAL_ERRORS)
      showSuccess('Callback Request Submitted')
    } catch (error: unknown) {
      // graceful user message
      showError('Callback Request Failed')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
        <>
         <main className='w-full px-[15px] lg:px-[] py-[24px] md:py-[94px] xl:px-[5rem] bg-[#fff0f7]'>
          <section
    className="
      flex flex-col
      md:flex-row md:justify-between md:items-center
      gap-[1.3rem]
      mb-[20px]
      mx-auto
    "
  >
    <div className="max-w-[454px] mx-auto md:mx-0 text-center md:text-left">
      <header>
        <h1
          className="
            text-left
            text-[24px] sm:text-[20px] md:text-[27px]
            font-[500] leading-[1.25]
           text-gradient-ll
            text-transparent bg-clip-text
            font-montserrat
          "
        >
          LoveLocal brings you the <span className="font-[800]">freshest groceries</span> delivered by the{' '}
          <span className="font-[800]">local shops you trust & love</span>
        </h1>
      </header>

      <div className="max-w-[300px] sm:mt-[30px] mt-[10px] mx-auto md:mx-0">
         <CustomImage
        imageStyle="w-[220px] sm:w-[280px] md:w-[328px]"
        imageType="CUSTOM"
        imageUrl="/images/image-callback.png"
      />
      </div>
    </div>

    <div className="relative flex justify-end sm:justify-center md:block">
      <div className="bg-white w-full max-w-[420px] rounded-3xl p-8 relative">

        <h2 className="text-3xl font-bold text-center text-[#9c1c6b] mb-6 font-extrabold">
          Request a Callback
        </h2>

        <form onSubmit={(e) => {
          void handleSubmit(e)
        }} className="space-y-6">

          <div className="relative group">
            <label
              htmlFor="fullName"
              className="absolute -top-2.5 left-4 bg-white px-1 text-[10px] font-black text-gray-600 uppercase tracking-wide"
            >
              Full Name *
            </label>
            <input
              maxLength={50}
              type="text"
              id="fullName"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              placeholder="Ex. Rohit"
              className={`w-full border-[1.5px] ${errors.fullName.length > 0 ? 'border-red-500' : 'border-gray-500'} rounded-xl px-4 py-3.5 text-gray-800 text-lg focus:outline-none focus:border-[#bc2086] transition-colors placeholder-gray-300`}
            />
            {errors.fullName.length > 0 && <p className="text-red-500 text-xs mt-1 ml-2">{errors.fullName}</p>}
          </div>

          <div className="relative group">
            <label
              htmlFor="mobile"
              className="absolute -top-2.5 left-4 bg-white px-1 text-[10px] font-bold text-gray-600 uppercase tracking-wide"
            >
              Mobile Number *
            </label>
            <div className={`flex items-center w-full border-[1.5px] ${errors.mobile.length > 0 ? 'border-red-500' : 'border-gray-500'} rounded-xl px-4 py-3.5 focus-within:border-[#bc2086] transition-colors`}>
              <span className="text-gray-800 text-lg mr-2">+91</span>
              <input
                type="text"
                id="mobile"
                name="mobile"
                maxLength={10}
                value={formData.mobile}
                onChange={handleChange}
                placeholder="99222 10000"
                className="w-full text-gray-800 text-lg focus:outline-none placeholder-gray-300"
              />
            </div>
            {errors.mobile.length > 0 && <p className="text-red-500 text-xs mt-1 ml-2">{errors.mobile}</p>}
          </div>

          <div className="relative group">
            <label
              htmlFor="email"
              className="absolute -top-2.5 left-4 bg-white px-1 text-[10px] font-bold text-gray-600 uppercase tracking-wide"
            >
              Email *
            </label>
            <input
              maxLength={50}
              type="text"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Ex. dasgupta@example.com"
              className={`w-full border-[1.5px] ${errors.email.length > 0 ? 'border-red-500' : 'border-gray-500'} rounded-xl px-4 py-3.5 text-gray-800 text-lg focus:outline-none focus:border-[#bc2086] transition-colors placeholder-gray-300`}
            />
            {errors.email.length > 0 && <p className="text-red-500 text-xs mt-1 ml-2">{errors.email}</p>}
          </div>

          <p className="text-[10px] text-gray-500 leading-[15px] px-1 !mt-4 text-center">
            By submitting this form, I consent to receive communications from LoveLocal through WhatsApp, SMS, email, phone calls, and other channels, even if my number is registered on DND/NDNC.
          </p>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-[#bc2086] hover:bg-[#a01b72] text-white text-xl py-3 rounded-2xl flex items-center justify-center gap-2 transition-all duration-200 shadow-md active:scale-[0.98] !mt-4 font-black"
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
            {!isSubmitting && (
               <div className="rounded-full p-0.5">
                 {/* <ArrowRight className="text-[#bc2086] w-4 h-4" strokeWidth={3} /> */}
                 <Icons name='circle-arrow-right' fill='white'/>
               </div>
            )}
          </button>

        </form>
      </div>
    </div>
          </section>
          </main>
        </>
  )
}

export default EnquiryForm
